import React, { useState, useEffect, useContext } from "react";
import {
  Segment,
  Label,
  Input,
  Icon,
  TextArea,
  Popup,
  Button,
  Confirm,
} from "semantic-ui-react";
import { SlidesContextEdit } from "../../../../../../../contexts/MediaContext";
import "../TemplateManager.scss";

export const Sev01 = React.memo((props) => {
  const { index, slideId, onLetterByOrder, onRemoveSlideByIndex } = props;
  const { slidesActiveSessionEdit } = useContext(SlidesContextEdit);
  const [showConfirm, setShowConfirm] = useState(false);
  const [time, setTime] = useState("");
  const [question, setQuestion] = useState("");
  const [choices, setChoices] = useState([]);

  const onOpenCloseConfirm = () => setShowConfirm((prevState) => !prevState);

  /**
   * Synchronizes the component state with the current slide's content in an active session.
   *
   * This `useEffect` hook is activated whenever the `slideId` changes. It searches for the slide
   * with the corresponding `slideId` within the `slidesActiveSessionEdit` array. If a matching slide
   * is found and it contains content, the hook updates the component's state to reflect the slide's
   * content. Specifically, it sets the `time`, `question`, and `choices` states with the respective
   * values from the slide's content.
   *
   * This ensures that the component always displays the most up-to-date information for the current
   * slide, facilitating a dynamic and interactive session editing experience.
   *
   * @dependency slideId - The ID of the current slide, changes to which trigger the hook.
   */
  useEffect(() => {
    const slide = slidesActiveSessionEdit.find(
      (slide) => slide._id === slideId
    );
    if (slide && slide.content) {
      setTime(slide.content.time);
      setQuestion(slide.content.question);
      setChoices(slide.content.choices);
    }
  }, [slideId]);

  /**
   * Updates the `time` and `question` content of a specific slide within the `slidesActiveSessionEdit` array.
   *
   * This `useEffect` hook is executed whenever there are changes to the `time` or `question` states. It begins by
   * locating the index of the slide that matches the `slideId` within the `slidesActiveSessionEdit` array. If the
   * slide is found (indicated by `slideIndex` not being -1), the hook proceeds to update the `time` and `question`
   * properties of the slide's content with the new values.
   *
   * This operation ensures that any modifications to the time allocated for the slide or the question posed in the
   * slide are immediately reflected within the active session's slide array. This is crucial for maintaining the
   * integrity and accuracy of the session content as it is being edited.
   *
   * @dependency time - The current time value to be updated for the slide.
   * @dependency question - The current question content to be updated for the slide.
   */
  useEffect(() => {
    const slideIndex = slidesActiveSessionEdit.findIndex(
      (slide) => slide._id === slideId
    );
    if (slideIndex !== -1) {
      slidesActiveSessionEdit[slideIndex].content.time = time;
      slidesActiveSessionEdit[slideIndex].content.question = question;
    }
  }, [time, question]);

  /**
   * Event handlers for updating slide content in `Sev01.js`.
   *
   * These functions are responsible for handling changes to the time, question, and choices of a slide within the `Sev01` component. They update the component's state based on user input, ensuring the slide's content reflects the latest edits.
   *
   * `handleTimeChange`:
   * - Triggered when the time input field changes.
   * - Updates the `time` state with the new value entered by the user.
   *
   * `handleQuestionChange`:
   * - Activated upon changes to the question input field.
   * - Updates the `question` state, ensuring it starts with a non-whitespace character by using `trimStart()`.
   *
   * `handleChoiceChange`:
   * - Called when any choice input field is modified.
   * - Receives the index of the choice being edited and the event object.
   * - Creates a new array of choices to maintain immutability, updates the specific choice's `answer` with the new value (also applying `trimStart()`), and then updates the `choices` state with this new array.
   *
   * These handlers are essential for interactive slide editing, allowing users to modify the time, question, and multiple choice answers directly within the UI, with changes reflected in real-time.
   */
  const handleTimeChange = (event) => {
    setTime(event.target.value);
  };

  const handleQuestionChange = (event) => {
    setQuestion(event.target.value.trimStart());
  };

  const handleChoiceChange = (index, event) => {
    const newChoices = [...choices];
    newChoices[index].answer = event.target.value.trimStart();
    setChoices(newChoices);
  };

  return (
    <>
      <Segment raised className="media-slide">
        <div className="media-slide__header">
          <div className="media-slide__header">
            <Label attached="top left">
              Hoja {index + 1} de {slidesActiveSessionEdit.length}
            </Label>
          </div>
          <div className="media-slide__button">
            <Popup
              content="Eliminar hoja"
              trigger={
                <Button
                  compact
                  color="grey"
                  icon="trash alternate outline"
                  onClick={onOpenCloseConfirm}
                />
              }
            />
          </div>
        </div>
        <div className="media-slide__time">
          <div className="media-slide__label-div">
            <Icon name="clock outline" />
            <h5 className="media-slide__label">Duración:</h5>
          </div>
          <Input
            className="media-slide__input-time"
            labelPosition="right"
            type="number"
            min="1"
            name="time"
            value={time}
            onChange={handleTimeChange}
          >
            <input />
            <Label className="media-slide__input-time-label">minuto(s)</Label>
          </Input>
        </div>
        <div className="row">
          <div className="media-slide__label-div">
            <Icon name="question" />
            <h5 className="media-slide__label">Pregunta:</h5>
          </div>
          <TextArea
            className="media-slide__textarea"
            rows={5}
            name="question"
            value={question}
            onChange={handleQuestionChange}
          />
          <div>
            {choices.map((choice, choiceIndex) => (
              <div className="col-md-12 mb-1" key={choiceIndex}>
                <div className="input-group border">
                  <Input
                    className="media-slide__input-choice"
                    labelPosition="left"
                    value={choice.answer}
                    onChange={(e) => handleChoiceChange(choiceIndex, e)}
                  >
                    <Label
                      className={`media-slide__input-label${
                        choice.correct ? "-success" : ""
                      }`}
                    >
                      {onLetterByOrder(choiceIndex + 1)}.
                    </Label>
                    <input />
                  </Input>
                </div>
              </div>
            ))}
          </div>
        </div>
      </Segment>
      <Confirm
        confirmButton="Aceptar"
        cancelButton="Cancelar"
        open={showConfirm}
        onCancel={onOpenCloseConfirm}
        onConfirm={() => onRemoveSlideByIndex(index)}
        content={`¿Deseas eliminar esta hoja?`}
        size="mini"
      />
    </>
  );
});
